import { Box, Flex, Spinner } from '@chakra-ui/react';
import { Text } from '@kula-ai/components';
import { AlertCircle } from '@kula-ai/icons-core';
import type { FlowBlockData } from '../../../types';

interface FlowBlockProps {
  data: FlowBlockData;
}

// Multi-step progress card. Same status vocabulary as the StepTimeline:
// pending = hollow square (a checklist item not yet started), active =
// spinner, done = filled check, error = red alert.
const StepGlyph = ({
  status,
}: {
  status: FlowBlockData['steps'][number]['status'];
}) => {
  if (status === 'active') return <Spinner size="xs" color="primary.500" />;
  if (status === 'error')
    return (
      <Box color="red.500" display="inline-flex">
        <AlertCircle size={13} />
      </Box>
    );
  if (status === 'done')
    return (
      <Box
        w="13px"
        h="13px"
        borderRadius="4px"
        bg="green.400"
        color="white"
        fontSize="10px"
        lineHeight="13px"
        textAlign="center"
        fontWeight={700}
      >
        ✓
      </Box>
    );
  // pending — hollow checklist square
  return (
    <Box
      w="13px"
      h="13px"
      borderRadius="4px"
      borderWidth="1.5px"
      borderColor="var(--secondary-300)"
    />
  );
};

export const FlowBlock = ({ data }: FlowBlockProps) => {
  if (!data?.steps?.length) return null;
  return (
    <Flex
      direction="column"
      gap="6px"
      px="12px"
      py="10px"
      borderRadius="8px"
      borderWidth="1px"
      borderColor="var(--secondary-200)"
      maxW="480px"
      data-test-id="agentforge-flow-block"
    >
      {data.title && (
        <Text variant="label" as="span">
          {data.title}
        </Text>
      )}
      {data.steps.map((step) => (
        <Flex key={step.id} align="center" gap="8px">
          <StepGlyph status={step.status} />
          <Text
            variant="p"
            as="span"
            className={
              step.status === 'pending'
                ? 'text-secondary-500'
                : 'text-secondary-900'
            }
          >
            {step.name}
          </Text>
          {typeof step.durationMs === 'number' && step.durationMs > 0 && (
            <Text variant="small" as="span" className="text-secondary-400">
              {(step.durationMs / 1000).toFixed(1)}s
            </Text>
          )}
        </Flex>
      ))}
    </Flex>
  );
};
